import { enhancedSearchService } from './enhancedSearchService';
import { rbiCollectionService } from './rbiCollectionService';
import { secondApiService } from './secondApiService';

export const MAX_BULK_FILES = 500;
export const MAX_BULK_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_BULK_TOTAL_BYTES = 1024 * 1024 * 1024;
export const BULK_RESULT_FLUSH_SIZE = 250;

export const BULK_PROVIDERS = Object.freeze([
  Object.freeze({
    id: 'rbiCollection',
    label: 'Custom Collection',
    description: 'Search the private RBI Rekognition collection.'
  }),
  Object.freeze({
    id: 'thirdPartyAPI1',
    label: 'Social Enhanced 1',
    description: 'Search the FaceCheck.ID-backed source.'
  }),
  Object.freeze({
    id: 'thirdPartyAPI2',
    label: 'Web Enhanced 2',
    description: 'Search the secondary web face source.'
  })
]);

const DEFAULT_PROVIDER_RUNNERS = {
  rbiCollection: ({ file, maxFaces, similarityThreshold, signal }) => (
    rbiCollectionService.searchCollection(
      file,
      maxFaces,
      similarityThreshold,
      { signal }
    )
  ),
  thirdPartyAPI1: ({ file, signal }) => (
    enhancedSearchService.analyzeImage(file, { signal })
  ),
  thirdPartyAPI2: ({ file, signal }) => (
    secondApiService.searchFaces(file, { signal })
  )
};

const providerById = new Map(BULK_PROVIDERS.map(provider => [provider.id, provider]));

export const isAbortError = error => (
  error?.name === 'AbortError' || error?.code === 'ERR_CANCELED'
);

const createAbortError = () => {
  const error = new Error('Bulk search cancelled.');
  error.name = 'AbortError';
  return error;
};

const getResponseMatches = response => {
  if (Array.isArray(response?.matches)) {
    if (
      response.matches.length === 0 &&
      response.error &&
      !/no matches/i.test(response.error)
    ) {
      throw new Error(response.error);
    }
    return response.matches;
  }

  if (response?.success === false || response?.enhancedResults === false) {
    throw new Error(response.error || 'Provider search failed.');
  }

  return [];
};

export const normalizeBulkMatches = ({
  response,
  file,
  fileIndex,
  providerId
}) => {
  const provider = providerById.get(providerId);
  if (!provider) throw new Error(`Unknown bulk-search provider: ${providerId}`);

  return getResponseMatches(response).map((match, matchIndex) => {
    const rawThumbnail = typeof match.thumbnail === 'string' ? match.thumbnail : '';
    const embeddedThumbnailOmitted = rawThumbnail.startsWith('data:');
    const numericScore = Number(match.score);

    return {
      id: `${fileIndex}-${providerId}-${match.id || 'match'}-${matchIndex}`,
      matchId: match.id || '',
      queryFileName: file.name,
      queryFileSize: Number(file.size) || 0,
      fileIndex,
      providerId,
      providerLabel: provider.label,
      nativeScore: Number.isFinite(numericScore) ? numericScore : 0,
      sourceUrl: match.url || '',
      thumbnailUrl: embeddedThumbnailOmitted ? '' : rawThumbnail,
      metadata: {
        ...(match.metadata || {}),
        ...(embeddedThumbnailOmitted ? { embeddedThumbnailOmitted: true } : {})
      }
    };
  });
};

export const validateBulkFiles = (incomingFiles, existingFiles = []) => {
  const accepted = [];
  const rejected = [];
  const seen = new Set(
    existingFiles.map(file => `${file.name}:${file.size}:${file.lastModified || 0}`)
  );
  let totalBytes = existingFiles.reduce((total, file) => total + (Number(file.size) || 0), 0);

  for (const file of Array.from(incomingFiles || [])) {
    const identity = `${file.name}:${file.size}:${file.lastModified || 0}`;
    const isSupportedType = ['image/jpeg', 'image/png'].includes(file.type) ||
      /\.(jpe?g|png)$/i.test(file.name);

    if (seen.has(identity)) {
      rejected.push({ fileName: file.name, reason: 'Duplicate file' });
    } else if (!isSupportedType) {
      rejected.push({ fileName: file.name, reason: 'Only JPEG and PNG images are supported' });
    } else if ((Number(file.size) || 0) > MAX_BULK_FILE_BYTES) {
      rejected.push({ fileName: file.name, reason: 'File exceeds the 10 MB limit' });
    } else if (existingFiles.length + accepted.length >= MAX_BULK_FILES) {
      rejected.push({ fileName: file.name, reason: `Batch limit is ${MAX_BULK_FILES} files` });
    } else if (totalBytes + (Number(file.size) || 0) > MAX_BULK_TOTAL_BYTES) {
      rejected.push({ fileName: file.name, reason: 'Batch exceeds the 1 GB total limit' });
    } else {
      accepted.push(file);
      seen.add(identity);
      totalBytes += Number(file.size) || 0;
    }
  }

  return { accepted, rejected };
};

export const runBulkFaceSearch = async ({
  files,
  providerIds,
  maxFaces = 10,
  similarityThreshold = 70,
  concurrency = 2,
  signal,
  onEvent = () => {},
  onResults = () => {},
  providerRunners = DEFAULT_PROVIDER_RUNNERS
}) => {
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('At least one image is required.');
  }
  if (!Array.isArray(providerIds) || providerIds.length === 0) {
    throw new Error('At least one search provider is required.');
  }

  const workerCount = Math.max(1, Math.min(Number(concurrency) || 1, 3, files.length));
  let nextFileIndex = 0;
  let pendingRows = [];

  const flushResults = () => {
    if (pendingRows.length === 0) return;
    const chunk = pendingRows;
    pendingRows = [];
    onResults(chunk);
  };

  const queueResults = rows => {
    if (rows.length === 0) return;
    pendingRows.push(...rows);
    if (pendingRows.length >= BULK_RESULT_FLUSH_SIZE) flushResults();
  };

  const processFile = async fileIndex => {
    const file = files[fileIndex];
    let failedProviders = 0;
    let completedProviders = 0;
    let totalMatches = 0;

    onEvent({ type: 'file-start', fileIndex });

    for (const providerId of providerIds) {
      if (signal?.aborted) {
        onEvent({ type: 'file-cancelled', fileIndex });
        throw createAbortError();
      }

      const runner = providerRunners[providerId];
      if (!runner) {
        failedProviders += 1;
        onEvent({
          type: 'provider-error',
          fileIndex,
          providerId,
          error: 'Provider is not available.'
        });
        continue;
      }

      onEvent({ type: 'provider-start', fileIndex, providerId });

      try {
        const response = await runner({
          file,
          fileIndex,
          maxFaces,
          similarityThreshold,
          signal
        });
        const rows = normalizeBulkMatches({ response, file, fileIndex, providerId });
        completedProviders += 1;
        totalMatches += rows.length;
        queueResults(rows);
        onEvent({
          type: 'provider-complete',
          fileIndex,
          providerId,
          matchCount: rows.length
        });
      } catch (error) {
        if (isAbortError(error) || signal?.aborted) {
          onEvent({ type: 'provider-cancelled', fileIndex, providerId });
          onEvent({ type: 'file-cancelled', fileIndex });
          throw createAbortError();
        }
        failedProviders += 1;
        onEvent({
          type: 'provider-error',
          fileIndex,
          providerId,
          error: error.message || 'Provider search failed.'
        });
      }
    }

    const status = failedProviders === providerIds.length
      ? 'failed'
      : failedProviders > 0
        ? 'partial'
        : 'completed';

    onEvent({
      type: 'file-complete',
      fileIndex,
      status,
      completedProviders,
      failedProviders,
      matchCount: totalMatches
    });
  };

  const worker = async () => {
    while (!signal?.aborted) {
      const fileIndex = nextFileIndex;
      nextFileIndex += 1;
      if (fileIndex >= files.length) return;
      try {
        await processFile(fileIndex);
      } catch (error) {
        if (isAbortError(error)) return;
        throw error;
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  flushResults();

  return { cancelled: Boolean(signal?.aborted) };
};

const escapeCsvCell = value => {
  const stringValue = value === null || value === undefined
    ? ''
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);
  const spreadsheetSafeValue = /^[=+\-@]/.test(stringValue)
    ? `'${stringValue}`
    : stringValue;
  return `"${spreadsheetSafeValue.replace(/"/g, '""')}"`;
};

export const bulkResultsToCsvParts = results => {
  const headers = [
    'queryFileName',
    'queryFileSize',
    'provider',
    'nativeScore',
    'sourceUrl',
    'thumbnailUrl',
    'matchId',
    'metadata'
  ];
  const parts = [`${headers.join(',')}\r\n`];

  for (const result of results) {
    parts.push(`${[
      result.queryFileName,
      result.queryFileSize,
      result.providerLabel,
      result.nativeScore,
      result.sourceUrl,
      result.thumbnailUrl,
      result.matchId,
      result.metadata
    ].map(escapeCsvCell).join(',')}\r\n`);
  }

  return parts;
};

export const bulkResultsToJsonlParts = results => (
  results.map(result => `${JSON.stringify(result)}\n`)
);

export const bulkFileReportToCsvParts = fileStatuses => {
  const parts = ['queryFileName,fileStatus,provider,providerStatus,matchCount,error\r\n'];

  for (const fileStatus of fileStatuses) {
    const providerEntries = Object.entries(fileStatus.providers || {});
    if (providerEntries.length === 0) {
      parts.push(`${[
        fileStatus.fileName,
        fileStatus.status,
        '',
        '',
        fileStatus.matchCount || 0,
        ''
      ].map(escapeCsvCell).join(',')}\r\n`);
      continue;
    }

    for (const [providerId, providerStatus] of providerEntries) {
      parts.push(`${[
        fileStatus.fileName,
        fileStatus.status,
        providerById.get(providerId)?.label || providerId,
        providerStatus.status,
        providerStatus.matchCount || 0,
        providerStatus.error || ''
      ].map(escapeCsvCell).join(',')}\r\n`);
    }
  }

  return parts;
};
